import {
    FormControl,
    FormLabel,
    Input,
    NumberInput,
    NumberInputField,
    Select,
    Text,
    Textarea,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FormErrorMessageWithSpacing from '../../../components/form-error-message';
import FormModal from '../../../components/FormModal';
import FormSection from '../../../components/form-section';
import FormStack from '../../../components/form-stack';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate, TranslationKeys } from '../../../i18n';
import { typography } from '../../../theme/tokens';
import {
    AchievementCategory,
    AchievementCriteriaType,
    AchievementRewardType,
    AchievementTier,
    CreateAchievementDefinitionDto,
    StreakActivityType,
    StreakPeriodType,
} from '../../../models/achievement/dto/achievement.dto';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useListCoupons } from '../../coupon-management/hooks/useListCoupons';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateAchievement } from '../hooks/useCreateAchievement';

type CreateAchievementInput = {
    name: string;
    description: string;
    category: AchievementCategory;
    tier: AchievementTier | '';
    criteriaType: AchievementCriteriaType;
    criteriaThreshold: number;
    criteriaSport: string;
    criteriaActivityType: StreakActivityType | '';
    criteriaPeriodType: StreakPeriodType | '';
    sportType: string;
    iconUrl: string;
    sortOrder: number;
    rewardType: AchievementRewardType;
    rewardValue: number;
    rewardCouponId: string;
};

const defaultValues: CreateAchievementInput = {
    name: '',
    description: '',
    category: AchievementCategory.GAMEPLAY,
    tier: '',
    criteriaType: 'games_played',
    criteriaThreshold: 1,
    criteriaSport: '',
    criteriaActivityType: '',
    criteriaPeriodType: '',
    sportType: '',
    iconUrl: '',
    sortOrder: 0,
    rewardType: AchievementRewardType.NONE,
    rewardValue: 0,
    rewardCouponId: '',
};

const CATEGORY_LABELS: Record<AchievementCategory, TranslationKeys> = {
    [AchievementCategory.GAMEPLAY]: 'achievement.category_gameplay',
    [AchievementCategory.SOCIAL]: 'achievement.category_social',
    [AchievementCategory.PROGRESSION]: 'achievement.category_progression',
    [AchievementCategory.COMMUNITY]: 'achievement.category_community',
    [AchievementCategory.CUSTOM]: 'achievement.category_custom',
};

const TIER_LABELS: Record<AchievementTier, TranslationKeys> = {
    [AchievementTier.BRONZE]: 'achievement.tier_bronze',
    [AchievementTier.SILVER]: 'achievement.tier_silver',
    [AchievementTier.GOLD]: 'achievement.tier_gold',
    [AchievementTier.PLATINUM]: 'achievement.tier_platinum',
};

const CRITERIA_TYPES: { value: AchievementCriteriaType; labelKey: TranslationKeys }[] = [
    { value: 'games_played', labelKey: 'achievement.criteria_games_played' },
    { value: 'courts_booked', labelKey: 'achievement.criteria_courts_booked' },
    { value: 'events_joined', labelKey: 'achievement.criteria_events_joined' },
    { value: 'coaching_sessions', labelKey: 'achievement.criteria_coaching_sessions' },
    { value: 'matches_won', labelKey: 'achievement.criteria_matches_won' },
    { value: 'streak', labelKey: 'achievement.criteria_streak' },
    { value: 'unique_opponents', labelKey: 'achievement.criteria_unique_opponents' },
    { value: 'level_reached', labelKey: 'achievement.criteria_level_reached' },
];

interface CreateAchievementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function CreateAchievementModal({ isOpen, onClose }: CreateAchievementModalProps) {
    const { mutateAsync: createAchievement, isLoading } = useCreateAchievement();
    const { selectedOrganization } = useSelectedOrganization();
    // `isError` matters because `rewardCouponId` is REQUIRED for a coupon
    // reward: a refused read would otherwise leave the operator staring at a
    // select holding only its placeholder, unable to submit and unable to see
    // why. See `coupon-read-error-gate.test.ts`.
    //
    // R118-5 — a CONVERSION, not a live fix, and labelled as one (D62). The
    // never-issued arm below is UNREACHABLE from the only caller: the sole
    // mount site is `AchievementListPage`, which routes its own
    // `useListAchievements` through `classifyRead` and early-returns a
    // `ScopePrompt` at `never-issued`, so the page cannot render the button
    // that opens this modal while the organization term is unsatisfied. That
    // premise is pinned in `coupon-read-error-state.test.tsx` — the arm passing
    // is not evidence of anything, the CALLER's guard is.
    // What the file genuinely gains is the in-flight arm: the select used to
    // render with zero options mid-read, indistinguishable from an org with no
    // coupons, on a field the form requires.
    const {
        data: coupons,
        isInitialLoading: isCouponsLoading,
        isError: isCouponsError,
    } = useListCoupons(selectedOrganization?.id || '');
    const couponsReadState = classifyRead(isCouponsError, isCouponsLoading, coupons);
    const [formData, setFormData] = useState<CreateAchievementInput>(defaultValues);
    const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
    const toast = useToast();

    useEffect(() => {
        if (isOpen) {
            setFormData(defaultValues);
            setErrors({});
        }
    }, [isOpen]);

    const handleInputChange = (field: keyof CreateAchievementInput, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = translate('achievement.name_required');
        }
        if (formData.criteriaThreshold < 1) {
            newErrors.criteriaThreshold = translate('achievement.threshold_min');
        }
        if (formData.criteriaType === 'streak' && !formData.criteriaPeriodType) {
            newErrors.criteriaPeriodType = translate('achievement.period_type_required');
        }
        if (formData.rewardType === AchievementRewardType.WALLET_CREDITS && (!formData.rewardValue || formData.rewardValue <= 0)) {
            newErrors.rewardValue = translate('achievement.reward_value_required');
        }
        if (formData.rewardType === AchievementRewardType.COUPON && !formData.rewardCouponId) {
            newErrors.rewardCouponId = translate('achievement.reward_coupon_required');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            await createAchievement({
                name: formData.name,
                description: formData.description || undefined,
                category: formData.category,
                tier: formData.tier || undefined,
                criteria: {
                    type: formData.criteriaType,
                    threshold: formData.criteriaThreshold,
                    sport: formData.criteriaSport || undefined,
                    ...(formData.criteriaType === 'streak' ? {
                        activityType: formData.criteriaActivityType || undefined,
                        periodType: formData.criteriaPeriodType || undefined,
                    } : {}),
                },
                sportType: formData.sportType || undefined,
                iconUrl: formData.iconUrl || undefined,
                sortOrder: formData.sortOrder,
                rewardType: formData.rewardType !== AchievementRewardType.NONE ? formData.rewardType : undefined,
                rewardValue: formData.rewardType === AchievementRewardType.WALLET_CREDITS ? formData.rewardValue : undefined,
                rewardCouponId: formData.rewardType === AchievementRewardType.COUPON ? formData.rewardCouponId || undefined : undefined,
            } as CreateAchievementDefinitionDto);
            toast({
                title: translate('achievement.created_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();
        } catch (error: any) {
            toast({
                title: translate('achievement.create_error'),
                description: error.message || '',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            title={translate('achievement.create_achievement')}
            onSubmit={handleSubmit}
            submitLabel={translate('achievement.create')}
            cancelLabel={translate('common.cancel')}
            isSubmitting={isLoading}
        >
                    <FormStack density="section">
                        {/* Basic info */}
                        <FormSection columns={{ base: 1, md: 2 }}>
                            <FormControl isInvalid={!!errors.name}>
                                <FormLabel {...typography.formLabel}>{translate('achievement.name')}</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder={translate('achievement.name_placeholder')}
                                    maxLength={200}
                                />
                                <FormErrorMessageWithSpacing title={errors.name || ''} />
                            </FormControl>

                            <FormControl>
                                <FormLabel {...typography.formLabel}>{translate('achievement.category')}</FormLabel>
                                <Select
                                    value={formData.category}
                                    onChange={(e) => handleInputChange('category', e.target.value)}
                                >
                                    {Object.values(AchievementCategory).map((cat) => (
                                        <option key={cat} value={cat}>{translate(CATEGORY_LABELS[cat])}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormSection>

                        <FormControl>
                            <FormLabel {...typography.formLabel}>{translate('achievement.description')}</FormLabel>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder={translate('achievement.description_placeholder')}
                                maxLength={1000}
                                rows={3}
                            />
                        </FormControl>

                        <FormSection label={translate('achievement.tier') || 'Tier'} columns={{ base: 1, md: 2 }}>
                            <FormControl>
                                <FormLabel {...typography.formLabel}>{translate('achievement.tier')}</FormLabel>
                                <Select
                                    value={formData.tier}
                                    onChange={(e) => handleInputChange('tier', e.target.value)}
                                    placeholder={translate('achievement.no_tier')}
                                >
                                    {Object.values(AchievementTier).map((tier) => (
                                        <option key={tier} value={tier}>{translate(TIER_LABELS[tier])}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel {...typography.formLabel}>{translate('achievement.sort_order')}</FormLabel>
                                <NumberInput
                                    value={formData.sortOrder}
                                    onChange={(_, val) => handleInputChange('sortOrder', isNaN(val) ? 0 : val)}
                                    min={0}
                                >
                                    <NumberInputField />
                                </NumberInput>
                            </FormControl>
                        </FormSection>

                        {/* Criteria */}
                        <FormSection label={translate('achievement.criteria_type') || 'Criteria'} columns={{ base: 1, md: 2 }}>
                            <FormControl>
                                <FormLabel {...typography.formLabel}>{translate('achievement.criteria_type')}</FormLabel>
                                <Select
                                    value={formData.criteriaType}
                                    onChange={(e) => handleInputChange('criteriaType', e.target.value)}
                                >
                                    {CRITERIA_TYPES.map((ct) => (
                                        <option key={ct.value} value={ct.value}>{translate(ct.labelKey)}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl isInvalid={!!errors.criteriaThreshold}>
                                <FormLabel {...typography.formLabel}>{translate('achievement.threshold')}</FormLabel>
                                <NumberInput
                                    value={formData.criteriaThreshold}
                                    onChange={(_, val) => handleInputChange('criteriaThreshold', isNaN(val) ? 1 : Math.round(val))}
                                    min={1}
                                >
                                    <NumberInputField />
                                </NumberInput>
                                <FormErrorMessageWithSpacing title={errors.criteriaThreshold || ''} />
                            </FormControl>
                        </FormSection>

                        <FormControl>
                            <FormLabel {...typography.formLabel}>{translate('achievement.sport_filter')}</FormLabel>
                            <Input
                                value={formData.criteriaSport}
                                onChange={(e) => handleInputChange('criteriaSport', e.target.value)}
                                placeholder={translate('achievement.sport_filter_placeholder')}
                            />
                        </FormControl>

                        {formData.criteriaType === 'streak' && (
                            <FormSection columns={{ base: 1, md: 2 }}>
                                <FormControl>
                                    <FormLabel {...typography.formLabel}>{translate('achievement.streak_activity_type')}</FormLabel>
                                    <Select
                                        value={formData.criteriaActivityType}
                                        onChange={(e) => handleInputChange('criteriaActivityType', e.target.value)}
                                    >
                                        <option value="">{translate('achievement.activity_any')}</option>
                                        {Object.values(StreakActivityType).map((at) => (
                                            <option key={at} value={at}>{at}</option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl isInvalid={!!errors.criteriaPeriodType}>
                                    <FormLabel {...typography.formLabel}>{translate('achievement.streak_period_type')}</FormLabel>
                                    <Select
                                        value={formData.criteriaPeriodType}
                                        onChange={(e) => handleInputChange('criteriaPeriodType', e.target.value)}
                                    >
                                        <option value="">{translate('common.select')}</option>
                                        {Object.values(StreakPeriodType).map((pt) => (
                                            <option key={pt} value={pt}>{translate(`achievement.period_${pt.toLowerCase()}` as TranslationKeys)}</option>
                                        ))}
                                    </Select>
                                    <FormErrorMessageWithSpacing title={errors.criteriaPeriodType || ''} />
                                </FormControl>
                            </FormSection>
                        )}

                        {/* Appearance */}
                        <FormSection label={translate('achievement.icon_url') || 'Appearance'}>
                            <FormControl>
                                <FormLabel {...typography.formLabel}>{translate('achievement.icon_url')}</FormLabel>
                                <Input
                                    value={formData.iconUrl}
                                    onChange={(e) => handleInputChange('iconUrl', e.target.value)}
                                    placeholder="https://..."
                                />
                            </FormControl>
                        </FormSection>

                        {/* Rewards */}
                        <FormSection label={translate('achievement.reward_type') || 'Rewards'}>
                            <FormControl>
                                <FormLabel {...typography.formLabel}>{translate('achievement.reward_type')}</FormLabel>
                                <Select
                                    value={formData.rewardType}
                                    onChange={(e) => handleInputChange('rewardType', e.target.value)}
                                >
                                    <option value={AchievementRewardType.NONE}>{translate('achievement.reward_none')}</option>
                                    <option value={AchievementRewardType.WALLET_CREDITS}>{translate('achievement.reward_wallet_credits')}</option>
                                    <option value={AchievementRewardType.COUPON}>{translate('achievement.reward_coupon')}</option>
                                </Select>
                            </FormControl>
                        </FormSection>

                        {formData.rewardType === AchievementRewardType.WALLET_CREDITS && (
                            <FormControl isInvalid={!!errors.rewardValue}>
                                <FormLabel {...typography.formLabel}>{translate('achievement.reward_credit_amount')}</FormLabel>
                                <NumberInput
                                    value={formData.rewardValue}
                                    onChange={(_, val) => handleInputChange('rewardValue', isNaN(val) ? 0 : Math.round(val))}
                                    min={1}
                                >
                                    <NumberInputField />
                                </NumberInput>
                                <FormErrorMessageWithSpacing title={errors.rewardValue || ''} />
                            </FormControl>
                        )}

                        {formData.rewardType === AchievementRewardType.COUPON && (
                            <FormControl isInvalid={!!errors.rewardCouponId}>
                                <FormLabel {...typography.formLabel}>{translate('achievement.reward_coupon_select')}</FormLabel>
                                {couponsReadState === 'unreadable' ? (
                                    <InlineErrorState message={translate('common.unexpected_error')} />
                                ) : couponsReadState === 'loading' ? (
                                    <Text color="gray.500" fontSize="sm" py={2}>
                                        {translate('coupon.loading')}
                                    </Text>
                                ) : couponsReadState === 'never-issued' ? (
                                    // Unreachable from the only caller (see the
                                    // hook above); written for D62's reason and
                                    // pinned on the CALLER's guard. Borrowed
                                    // key — the catalogues are frozen (R96-7).
                                    <Text color="gray.500" fontSize="sm" py={2}>
                                        {translate(scopePromptI18nKey('organization') as any)}
                                    </Text>
                                ) : (
                                    <Select
                                        value={formData.rewardCouponId}
                                        onChange={(e) => handleInputChange('rewardCouponId', e.target.value)}
                                        placeholder={translate('achievement.reward_coupon_placeholder')}
                                    >
                                        {coupons?.map((coupon) => (
                                            <option key={coupon.id} value={coupon.id}>{coupon.name}</option>
                                        ))}
                                    </Select>
                                )}
                                <FormErrorMessageWithSpacing title={errors.rewardCouponId || ''} />
                            </FormControl>
                        )}
                    </FormStack>
        </FormModal>
    );
}
